// ─────────────────────────────────────────────────────────────────────────
// VentasPorPieza — el ranking de productos (27-ago-2026, pedido Maxi).
// El espejo de VentasPorCliente pero por PIEZA: cuál se vendió más (unidades)
// y cuál generó más ingresos, de mayor a menor. Fuente: GET /sii/ventas-piezas
// (PieceInstance: precio unitario × cantidad). Toggle ingreso ↔ unidades.
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import backend from "../../backend";
import { formatPrice } from "../../utils";

const fmt = n => `$${formatPrice(Math.round(n || 0))}`;
const num = { fontVariantNumeric: "tabular-nums" };

const TIPO = {
  ref: { label: "Catálogo", color: "#3498db" },
  fab: { label: "Fabricación", color: "#f39c12" },
  rep: { label: "Reparación", color: "#9b59b6" },
};

const S = {
  card: { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: 14 },
  label: { fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em" },
};

const VentasPorPieza = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [meses, setMeses] = useState(6);
  const [orden, setOrden] = useState("ingreso"); // ingreso | unidades
  const [buscar, setBuscar] = useState("");
  const [q, setQ] = useState("");                 // búsqueda aplicada (debounced)
  const [abierta, setAbierta] = useState(null);    // clave de la pieza expandida
  const [detalle, setDetalle] = useState({});      // cache clave → {clientes, trabajos}

  useEffect(() => { const t = setTimeout(() => setQ(buscar.trim()), 400); return () => clearTimeout(t); }, [buscar]);

  useEffect(() => {
    let vivo = true;
    setData(null); setAbierta(null);
    backend.get("sii/ventas-piezas", { params: { meses, buscar: q } })
      .then(r => { if (vivo) setData(r.data); })
      .catch(e => { if (vivo) setError(e?.response?.data?.error || e.message); });
    return () => { vivo = false; };
  }, [meses, q]);

  const abrir = async (it) => {
    if (abierta === it.clave) { setAbierta(null); return; }
    setAbierta(it.clave);
    if (!detalle[it.clave]) {
      try {
        const r = await backend.get("sii/ventas-piezas/detalle", { params: { clave: it.clave, esPieza: it.esPieza ? 1 : 0, meses } });
        setDetalle(d => ({ ...d, [it.clave]: r.data }));
      } catch (e) { setDetalle(d => ({ ...d, [it.clave]: { error: e?.response?.data?.error || e.message } })); }
    }
  };

  if (error) return <div style={{ color: "#e74c3c", fontSize: 12, padding: 12 }}>Error: {error}</div>;
  if (!data) return <div style={{ color: "#8a8a8a", fontSize: 12, padding: 12 }}>Calculando el ranking de piezas…</div>;

  const items = [...data.items].sort((a, b) => orden === "unidades" ? b.unidades - a.unidades : b.ingreso - a.ingreso);
  const maxVal = Math.max(1, ...items.map(i => orden === "unidades" ? i.unidades : i.ingreso));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Resumen + controles */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ ...S.card, flex: "1 1 160px" }}>
          <div style={S.label}>Ingreso del período</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#2ecc71", ...num }}>{fmt(data.totalIngreso)}</div>
          <div style={{ fontSize: 10, color: "#8a8a8a", ...num }}>{data.totalUnidades.toLocaleString("es-CL")} unidades · {items.length} piezas distintas</div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[3, 6, 12].map(m => (
            <button key={m} onClick={() => setMeses(m)} className="chip-touch no-touch-min t-02"
              style={{ background: meses === m ? "#3a1114" : "#1e1e1e", color: meses === m ? "#ff8a8f" : "#999",
                border: `1px solid ${meses === m ? "#e30613" : "#333"}`, borderRadius: 6, padding: "6px 12px", fontSize: 11, cursor: "pointer" }}>
              {m} meses
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {[["ingreso", "Por ingresos"], ["unidades", "Por unidades"]].map(([k, l]) => (
            <button key={k} onClick={() => setOrden(k)} className="chip-touch no-touch-min t-02"
              style={{ background: orden === k ? "#0d2035" : "#1e1e1e", color: orden === k ? "#3498db" : "#999",
                border: `1px solid ${orden === k ? "#3498db" : "#333"}`, borderRadius: 6, padding: "6px 12px", fontSize: 11, cursor: "pointer" }}>
              {l}
            </button>
          ))}
        </div>
        {/* Buscador por pieza/código (pedido Maxi) */}
        <input value={buscar} onChange={e => setBuscar(e.target.value)}
          placeholder="🔎 Filtrar por pieza o código (SKU)…"
          style={{ flex: "1 1 200px", background: "#161616", border: "1px solid #333", borderRadius: 6, padding: "8px 11px", fontSize: 12, color: "#ddd" }} />
      </div>

      {/* Ranking */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {items.map((it, idx) => {
          const val = orden === "unidades" ? it.unidades : it.ingreso;
          const t = TIPO[it.tipoTrabajo] || TIPO.ref;
          const esAbierta = abierta === it.clave;
          const det = detalle[it.clave];
          return (
            <div key={idx} style={{ ...S.card, padding: 0, position: "relative", overflow: "hidden", border: `1px solid ${esAbierta ? "#3a5a7a" : "#2e2e2e"}` }}>
              <div onClick={() => abrir(it)} className="tap-card" style={{ padding: "10px 14px", position: "relative", cursor: "pointer" }}>
                {/* barra proporcional de fondo */}
                <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${(val / maxVal) * 100}%`,
                  background: orden === "unidades" ? "#3498db14" : "#2ecc7114", zIndex: 0 }} />
                <div style={{ display: "flex", alignItems: "center", gap: 10, position: "relative", zIndex: 1 }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: idx < 3 ? "#e30613" : "#666", width: 26, textAlign: "right", ...num }}>{idx + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {it.nombre}
                      {it.tipoTrabajo !== "ref" && <span style={{ fontSize: 10, color: t.color, marginLeft: 6, border: `1px solid ${t.color}55`, borderRadius: 4, padding: "1px 5px" }}>{t.label}</span>}
                    </div>
                    <div style={{ fontSize: 10, color: "#8a8a8a", ...num }}>
                      {it.sku ? `SKU ${it.sku} · ` : ""}{it.unidades.toLocaleString("es-CL")} u · {it.nTrabajos} trabajos · c/u {fmt(it.precioProm)}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: orden === "unidades" ? "#3498db" : "#2ecc71", ...num }}>
                      {orden === "unidades" ? it.unidades.toLocaleString("es-CL") + " u" : fmt(it.ingreso)}
                    </div>
                    <div style={{ fontSize: 10, color: "#8a8a8a", ...num }}>{it.pct}% del total {esAbierta ? "▲" : "▼"}</div>
                  </div>
                </div>
              </div>
              {esAbierta && (
                <div style={{ borderTop: "1px solid #2e2e2e", background: "#1f1f1f", padding: "10px 14px" }}>
                  {!det ? <div style={{ fontSize: 11, color: "#8a8a8a" }}>Cargando detalle…</div>
                  : det.error ? <div style={{ fontSize: 11, color: "#e74c3c" }}>{det.error}</div>
                  : (
                    <>
                      <div style={{ ...S.label, marginBottom: 6 }}>Ventas por cliente de esta pieza</div>
                      {det.clientes.map((c, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", borderBottom: "1px solid #282828" }}>
                          <span style={{ color: "#ddd" }}>{c.cliente} <span style={{ color: "#666", fontSize: 10 }}>· {c.unidades} u · {c.nTrabajos} trab</span></span>
                          <span style={{ color: "#2ecc71", fontWeight: 700, ...num }}>{fmt(c.ingreso)}</span>
                        </div>
                      ))}
                      <div style={{ ...S.label, margin: "10px 0 6px" }}>Trabajos / OTs donde salió</div>
                      {det.trabajos.slice(0, 30).map((tr, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, padding: "3px 0", color: "#b8b8b8" }}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>
                            {tr.ot ? `OT ${tr.ot}` : "—"} · {tr.cliente} <span style={{ color: "#666" }}>· {tr.unidades} u</span>
                          </span>
                          <span style={{ ...num, flexShrink: 0, marginLeft: 8 }}>{fmt(tr.ingreso)}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {!items.length && <div style={{ ...S.card, textAlign: "center", color: "#8a8a8a", fontSize: 12 }}>Sin piezas en el período{q ? " para esa búsqueda" : ""}.</div>}
      </div>
      <div style={{ fontSize: 10, color: "#8a8a8a", padding: "4px 2px" }}>
        Cuenta las piezas que entraron a producción o se vendieron (precio × cantidad). Toca una pieza para ver quién la compró. Incluye catálogo, fabricaciones y reparaciones.
      </div>
    </div>
  );
};

export default VentasPorPieza;
